import React, { useState } from "react";
import { API_URLS } from "../../config/apiUrls";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Collapse, // Importação adicionada
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOpenIcon from "@mui/icons-material/LockOpen"; // Ícone para o botão

interface GerenciarSenhaProps {
  token: string;
}

const GerenciarSenha: React.FC<GerenciarSenhaProps> = ({ token }) => {
  // NOVO ESTADO para controlar a visibilidade do formulário
  const [showForm, setShowForm] = useState(false);

  // Estado para os campos de senha
  const [senhaAtual, setSenhaAtual] = useState<string>("");
  const [novaSenha, setNovaSenha] = useState<string>("");
  const [confirmarSenha, setConfirmarSenha] = useState<string>("");

  // Estado para mostrar ou esconder a senha
  const [mostrarSenhaAtual, setMostrarSenhaAtual] = useState<boolean>(false); // Adicionado para a senha atual
  const [mostrarNovaSenha, setMostrarNovaSenha] = useState<boolean>(false);
  const [mostrarConfirmarSenha, setMostrarConfirmarSenha] =
    useState<boolean>(false);

  // Estado para controle de mensagens de erro e sucesso
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);

  const logoutTrocaSenha = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("role");
    window.location.href = ""; // Redirecionar para a tela de login
  };

  // Limpa os campos e mensagens após fechar o formulário
  const resetForm = () => {
    setSenhaAtual("");
    setNovaSenha("");
    setConfirmarSenha("");
    setErro(null);
    setSucesso(null);
    setMostrarNovaSenha(false);
    setMostrarConfirmarSenha(false);
    setMostrarSenhaAtual(false);
  };

  // Função para obter o id do admin
  const fetchAdminId = async () => {
    // ... (lógica inalterada)
    try {
      const response = await fetch(API_URLS.role, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Erro ao buscar o perfil do administrador");
      }

      const data = await response.json();
      return data.id; // Retorna o ID do administrador
    } catch (error) {
      setErro("Erro ao buscar o perfil do administrador");
      throw error;
    }
  };

  // Função para alterar a senha
  const handleAlterarSenha = async () => {
    // Limpa mensagens anteriores
    setErro(null);
    setSucesso(null);

    if (novaSenha !== confirmarSenha) {
      setErro("As novas senhas não coincidem.");
      return;
    }

    if (!senhaAtual || !novaSenha || !confirmarSenha) {
      setErro("Por favor, preencha todos os campos de senha.");
      return;
    }

    try {
      // 1. Obter o ID do administrador
      const adminId = await fetchAdminId();

      // 2. Realizar o PUT para alterar a senha (incluindo a senha atual)
      const response = await fetch(`${API_URLS.gerenciar}/${adminId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          currentPassword: senhaAtual, // Deve ser a senha atual para validação
          newPassword: novaSenha,
        }),
      });

      if (!response.ok) {
        // Tenta obter mensagem de erro da API
        const errorData = await response.json();
        const apiError = errorData.message || "Erro ao alterar a senha.";
        throw new Error(apiError);
      }

      setSucesso("Senha alterada com sucesso! Você será desconectado.");
      setErro(null);
      // Esconde o formulário após o sucesso e limpa os campos
      setShowForm(false);
      resetForm();

      // Aguarda um momento antes de deslogar para o usuário ver a mensagem
      setTimeout(logoutTrocaSenha, 2000);
    } catch (error: any) {
      setErro(error.message || "Erro ao alterar a senha.");
      setSucesso(null);
    }
  };

  return (
    <Box
      sx={{
        padding: 3,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: 400,
        margin: "auto",
      }}
    >
      {/* Exibe mensagens de status */}
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}
      {sucesso && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {sucesso}
        </Alert>
      )}

      {/* BOTÃO PARA MOSTRAR/OCULTAR O FORMULÁRIO */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color={showForm ? "secondary" : "primary"}
          startIcon={<LockOpenIcon />}
          onClick={() => {
            setShowForm(!showForm);
            if (showForm) {
              // Limpa o formulário se estiver sendo fechado
              resetForm();
            }
          }}
        >
          {showForm ? "Cancelar Alteração" : "Alterar Minha Senha"}
        </Button>
      </Box>

      {/* COLLAPSE que envolve todos os campos do formulário */}
      <Collapse in={showForm}>
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: "20px", mt: 1 }}
        >
          {/* Campo SENHA ATUAL */}
          <TextField
            label="Senha Atual"
            type={mostrarSenhaAtual ? "text" : "password"}
            value={senhaAtual}
            onChange={(e) => setSenhaAtual(e.target.value)}
            fullWidth
            variant="outlined"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setMostrarSenhaAtual(!mostrarSenhaAtual)}
                    edge="end"
                  >
                    {mostrarSenhaAtual ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Campo NOVA SENHA */}
          <TextField
            label="Nova Senha"
            type={mostrarNovaSenha ? "text" : "password"}
            value={novaSenha}
            onChange={(e) => setNovaSenha(e.target.value)}
            fullWidth
            variant="outlined"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setMostrarNovaSenha(!mostrarNovaSenha)}
                    edge="end"
                  >
                    {mostrarNovaSenha ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Campo CONFIRMAR NOVA SENHA */}
          <TextField
            label="Confirmar Nova Senha"
            type={mostrarConfirmarSenha ? "text" : "password"}
            value={confirmarSenha}
            onChange={(e) => setConfirmarSenha(e.target.value)}
            fullWidth
            variant="outlined"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setMostrarConfirmarSenha(!mostrarConfirmarSenha)
                    }
                    edge="end"
                  >
                    {mostrarConfirmarSenha ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Botão de SUBMIT */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleAlterarSenha}
            sx={{ mt: 1 }}
          >
            Confirmar Alteração
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
};

export default GerenciarSenha;
